/** Detects shareable Google Maps links (short or long). */
const GOOGLE_MAPS_HOST_RE =
  /^(maps\.app\.goo\.gl|goo\.gl|maps\.google\.com|g\.page|www\.google\.[a-z.]+|google\.[a-z.]+)$/i;

const GOOGLE_MAPS_PATH_RE = /^\/maps|^\/maps\//;

/**
 * Returns true when the string looks like a Google Maps share URL.
 * Server-side validation still runs on the callable.
 */
export function isValidGoogleMapsUrl(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed.length) return false;

  try {
    const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withScheme);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();

    if (host === 'maps.app.goo.gl') return true;
    if (host === 'goo.gl' && url.pathname.startsWith('/maps')) return true;
    if (host === 'g.page') return true;
    if (host === 'maps.google.com') return true;

    if (GOOGLE_MAPS_HOST_RE.test(host) && GOOGLE_MAPS_PATH_RE.test(url.pathname)) {
      return true;
    }

    return host.includes('google.') && url.pathname.includes('/maps');
  } catch {
    return false;
  }
}

const PLACE_ID_PATTERNS: RegExp[] = [
  /[?&]query_place_id=([A-Za-z0-9_-]+)/i,
  /[?&]place_id=([A-Za-z0-9_-]+)/i,
  /place_id:([A-Za-z0-9_-]+)/i,
  /!1s(ChIJ[A-Za-z0-9_-]+)/i,
  /(ChIJ[A-Za-z0-9_-]{10,})/,
];

/** Short links need server-side redirect resolution (browser cannot follow goo.gl). */
export function isShortGoogleMapsLink(url: string): boolean {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, '').toLowerCase();
    return host === 'maps.app.goo.gl' || (host === 'goo.gl' && url.includes('/maps'));
  } catch {
    return false;
  }
}

export function extractPlaceIdFromUrl(url: string): string | null {
  for (const pattern of PLACE_ID_PATTERNS) {
    const match = url.match(pattern);
    const id = match?.[1]?.trim();
    if (id?.startsWith('ChIJ')) return id;
  }
  return null;
}

/** Prefer !3d!4d pin over @ viewport center. */
export function extractLatLngFromUrl(url: string): { lat: number; lng: number } | null {
  const pinMatch = url.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (pinMatch) {
    const lat = Number(pinMatch[1]);
    const lng = Number(pinMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const atMatch = url.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (atMatch) {
    const lat = Number(atMatch[1]);
    const lng = Number(atMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  return null;
}

export function extractPlaceNameFromUrl(url: string): string | null {
  const match = url.match(/\/place\/([^/@?]+)/i);
  if (!match?.[1]) return null;

  const name = decodeURIComponent(match[1].replace(/\+/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();

  if (name.length < 2) return null;
  if (/^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(name)) return null;
  return name;
}

/** Text Search query when URL has /place/… but no ChIJ nor lat,lng. */
export function extractPlaceTextQueryFromUrl(url: string): string | null {
  return extractPlaceNameFromUrl(url);
}

/** Normalizes user paste to an absolute https URL for the backend. */
export function normalizeGoogleMapsUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!isValidGoogleMapsUrl(trimmed)) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    return new URL(withScheme).toString();
  } catch {
    return null;
  }
}
